import { TSavedLaneSchema } from 'transfix-form/forms/SavedLaneForm/schema'
import {
  useGetFMSLaneAlertDetails,
  useDeleteCMLaneAlert,
  LaneAlert,
  useCreateLaneAlert,
  useUpdateCMLaneAlert,
  LaneAlertResponse,
} from 'apps/marketplace/apiTypes/carrierLaneAlerts'
import { getBookableShipmentSavedSearch } from '../utils'

interface IUseSavedLanesProps {
  carrierApplicationId: string
  savedLaneId?: number
}

interface IUseSavedLanesState {
  savedLanes: LaneAlert[] | undefined
  refetch: () => Promise<LaneAlertResponse | null>
  createSavedLane: (savedLaneFormData: TSavedLaneSchema) => Promise<boolean>
  deleteSavedLane: (savedLaneId: number) => Promise<boolean>
  updateSavedLane: (
    savedLaneFormData: TSavedLaneSchema,
    currentSubscribers: LaneAlert['recipientSubscribers']
  ) => Promise<boolean>
}

export const useSavedLanes = ({
  carrierApplicationId,
  savedLaneId,
}: IUseSavedLanesProps): IUseSavedLanesState => {
  const carrierId = Number(carrierApplicationId)

  /**
   * Fetching Saved Lanes
   */
  const { data, refetch } = useGetFMSLaneAlertDetails({
    carrierId,
  })
  const { laneAlerts: savedLanes } = data || {}

  /**
   * Restful Mutations
   */
  const { mutate: createSavedLaneMutation } = useCreateLaneAlert({ carrierId })
  const { mutate: deleteSavedLaneMutation } = useDeleteCMLaneAlert({ carrierId })
  const { mutate: updateSavedLaneMutation } = useUpdateCMLaneAlert({
    carrierId,
    id: savedLaneId || 0,
  })

  const createSavedLane = async (savedLaneFormData: TSavedLaneSchema) => {
    try {
      await createSavedLaneMutation({
        bookableShipmentSavedSearch: getBookableShipmentSavedSearch({
          savedLane: savedLaneFormData,
        }),
      })
      return true
    } catch {
      return false
    }
  }

  const deleteSavedLane = async (savedLaneId: number) => {
    try {
      await deleteSavedLaneMutation(`${savedLaneId}`)
      return true
    } catch {
      return false
    }
  }

  const updateSavedLane = async (
    savedLaneFormData: TSavedLaneSchema,
    currentSubscribers: LaneAlert['recipientSubscribers'] = []
  ) => {
    try {
      await updateSavedLaneMutation({
        bookableShipmentSavedSearch: getBookableShipmentSavedSearch({
          savedLane: savedLaneFormData,
          currentSubscribers,
        }),
      })
      return true
    } catch {
      return false
    }
  }

  return {
    savedLanes,
    refetch,
    createSavedLane,
    deleteSavedLane,
    updateSavedLane,
  }
}
